import axios from "axios";

// Resolve backend URL at runtime with defense-in-depth fallback:
// If REACT_APP_BACKEND_URL is missing, empty, or a placeholder (example.com),
// fall back to same-origin. Production frontend/backend share one origin with
// `/api/*` routed to backend via Kubernetes ingress, so window.location.origin
// is always the correct value when the env var is unusable.
const _ENV_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const _isUsable = (u) => typeof u === "string" && u.length > 0 && !/example\.com/i.test(u);
const BACKEND_URL = _isUsable(_ENV_BACKEND_URL)
  ? _ENV_BACKEND_URL
  : (typeof window !== "undefined" ? window.location.origin : "");
export const API_BASE = `${BACKEND_URL}/api`;

const STORAGE_KEY = "rentopc.auth";

export const getStoredAuth = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const setStoredAuth = (payload) => {
  if (payload) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } else {
    localStorage.removeItem(STORAGE_KEY);
  }
};

export const api = axios.create({ baseURL: API_BASE });

// Attach access token on every request.
api.interceptors.request.use((config) => {
  const auth = getStoredAuth();
  if (auth?.access_token) {
    config.headers.Authorization = `Bearer ${auth.access_token}`;
  }
  return config;
});

// Simple refresh-on-401 (single-flight).
let refreshInFlight = null;

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    if (error?.response?.status !== 401 || original._retried) {
      throw error;
    }
    const stored = getStoredAuth();
    if (!stored?.refresh_token) {
      setStoredAuth(null);
      throw error;
    }
    original._retried = true;
    if (!refreshInFlight) {
      refreshInFlight = axios
        .post(`${API_BASE}/auth/refresh`, { refresh_token: stored.refresh_token })
        .then((resp) => {
          setStoredAuth(resp.data);
          return resp.data;
        })
        .catch((e) => {
          setStoredAuth(null);
          throw e;
        })
        .finally(() => {
          refreshInFlight = null;
        });
    }
    const fresh = await refreshInFlight;
    original.headers.Authorization = `Bearer ${fresh.access_token}`;
    return api.request(original);
  }
);

export const login = async (identifier, password) => {
  // Identifier can be a username (preferred) or a full email (legacy).
  const key = identifier.includes("@") ? "email" : "username";
  const r = await axios.post(`${API_BASE}/auth/login`, { [key]: identifier, password });
  setStoredAuth(r.data);
  return r.data;
};

export const logout = async () => {
  const stored = getStoredAuth();
  if (stored?.refresh_token) {
    try {
      await axios.post(`${API_BASE}/auth/logout`, { refresh_token: stored.refresh_token });
    } catch (err) {
      // Server-side session revoke failed — we still clear local state below.
      if (process.env.NODE_ENV === "development") console.warn("logout: server revoke failed", err?.message || err);
    }
  }
  setStoredAuth(null);
};

export const me = async () => (await api.get("/auth/me")).data;

// Extract API error to a display-ready string.
export const errorToMessage = (err, fallback = "Something went wrong") => {
  const d = err?.response?.data?.detail;
  if (!d) return err?.message || fallback;
  if (typeof d === "string") return d;
  if (d.message) return d.message;
  if (Array.isArray(d)) {
    return d.map((v) => `${(v.loc || []).join(".")}: ${v.msg}`).join("; ");
  }
  return fallback;
};
